import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/shared/integrations/supabase/client";
import type { Json } from "@/shared/integrations/supabase/types";
import { useAuth } from "@/shared/hooks/useAuth";
import { Button } from "@/shared/components/ui/button";
import { LogOut, Building2, ArrowLeft, ArrowLeftRight } from "lucide-react";
import { estiloVariaveisTemaPortal } from "../lib/estiloVariaveisTemaPortal";

export interface ExternalLayoutContext {
  /** Recarrega instituição (ex.: após alterar portal_config no banco). */
  recarregarInstituicao: () => Promise<void>;
  institution: {
    id: string;
    name: string;
    nome_fantasia: string | null;
    type: string | null;
    logo_url: string | null;
    theme_colors: { primary?: string; secondary?: string; text?: string } | null;
    ia_config: Record<string, unknown> | null;
    areas_config: Record<string, unknown> | unknown[] | null;
    portal_config: Json | null;
    razao_social: string | null;
    cnpj: string | null;
    cep: string | null;
    cidade: string | null;
    estado: string | null;
    endereco: string | null;
    email: string | null;
    telefone: string | null;
    cliente_desde: string | null;
    custom_fields: unknown;
  };
  role: string | null;
  /** Cargo institucional (institution_users.cargo) do usuário logado no portal. */
  memberCargo: string | null;
  /** `institution_users.unit_id` quando existir (ex.: vínculo a unidade/CNPJ da padaria). */
  memberInstitutionUnitId: string | null;
  /** True quando o login é membro externo da instituição (ex.: institution_external) — restringe convites no portal. */
  portalUsuarioEhExternoDaInstituicao: boolean;
}

/** Mesmo critério das políticas RLS de institutions — pré-visualização pelo escritório. */
const PERFIS_VOLTAR_ESCRITORIO = new Set([
  "socio",
  "socio_gestor",
  "associado",
  "estagiario",
  "colaborador",
]);

/**
 * Layout para o portal externo de instituições.
 * Completamente isolado — sem sidebar interna nem header do dashboard.
 * Aplica CSS variables dinâmicas com as cores da instituição.
 */
export default function ExternalLayout() {
  const navigate = useNavigate();
  const { institutionId: paramId } = useParams<{ institutionId: string }>();
  const { user } = useAuth();
  /** Só o id no callback — evita reexecução do load ao TOKEN_REFRESHED (novo objeto `user`), que desmontava o portal e voltava para "Início". */
  const userId = user?.id ?? null;
  const [institution, setInstitution] = useState<ExternalLayoutContext["institution"] | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [memberCargo, setMemberCargo] = useState<string | null>(null);
  const [memberInstitutionUnitId, setMemberInstitutionUnitId] = useState<string | null>(null);
  const [portalUsuarioEhExternoDaInstituicao, setPortalUsuarioEhExternoDaInstituicao] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [qualificacaoPerfil, setQualificacaoPerfil] = useState<string | null>(null);
  const [temMultiplasInstituicoes, setTemMultiplasInstituicoes] = useState(false);

  const exibirVoltarEscritorio =
    qualificacaoPerfil != null &&
    qualificacaoPerfil !== "institution_external" &&
    PERFIS_VOLTAR_ESCRITORIO.has(qualificacaoPerfil);

  const recarregarInstituicao = useCallback(async () => {
    if (!paramId) return;
    const [res, profileRes] = await Promise.all([
      supabase.rpc("instituicao_por_id", { p_institution_id: paramId }),
      userId
        ? supabase.from("profiles").select("qualificacao").eq("user_id", userId).maybeSingle()
        : Promise.resolve({ data: null, error: null }),
    ]);
    const row =
      res.data && Array.isArray(res.data) && res.data.length > 0 ? res.data[0] : null;
    if (res.error || !row) {
      setInstitution(null);
      setRole(null);
      setMemberCargo(null);
      setMemberInstitutionUnitId(null);
      setPortalUsuarioEhExternoDaInstituicao(false);
      // RPC não retornou instituição — usuário logado volta ao seletor; demais casos: tela de erro.
      if (!row && userId) {
        navigate("/acesso-externo/portal", { replace: true });
        return;
      }
      setErro(true);
      return;
    }

    setErro(false);

    let extras: {
      nome_fantasia: string | null;
      razao_social: string | null;
      cnpj: string | null;
      cep: string | null;
      cidade: string | null;
      estado: string | null;
      endereco: string | null;
      email: string | null;
      telefone: string | null;
      cliente_desde: string | null;
      custom_fields: unknown;
    } | null = null;
    try {
      const extrasRes = await supabase
        .from("institutions")
        .select(
          "nome_fantasia, razao_social, cnpj, cep, cidade, estado, endereco, email, telefone, cliente_desde, custom_fields",
        )
        .eq("id", row.id)
        .maybeSingle();
      if (!extrasRes.error && extrasRes.data) extras = extrasRes.data;
    } catch {
      // extras fica null
    }

    setInstitution({
      id: row.id,
      name: row.name,
      nome_fantasia: extras?.nome_fantasia ?? null,
      type: row.type,
      logo_url: row.logo_url,
      theme_colors: (row.theme_colors ?? null) as {
        primary?: string;
        secondary?: string;
        text?: string;
      } | null,
      ia_config: (row.ia_config ?? null) as Record<string, unknown> | null,
      areas_config: (row.areas_config ?? null) as Record<string, unknown> | unknown[] | null,
      portal_config: (row.portal_config ?? null) as Json | null,
      razao_social: extras?.razao_social ?? null,
      cnpj: extras?.cnpj ?? null,
      cep: extras?.cep ?? null,
      cidade: extras?.cidade ?? null,
      estado: extras?.estado ?? null,
      endereco: extras?.endereco ?? null,
      email: extras?.email ?? null,
      telefone: extras?.telefone ?? null,
      cliente_desde: extras?.cliente_desde ?? null,
      custom_fields: extras?.custom_fields ?? null,
    });
    const PERFIS_INTERNOS = [
      "socio",
      "socio_gestor",
      "associado",
      "estagiario",
      "colaborador",
      "advogado",
      "administrativo",
      "contador",
    ];
    const qualifAtual = profileRes.data?.qualificacao ?? null;
    const userRole = row.user_role ?? null;
    setRole(userRole);
    // Equipe do escritório sem papel externo → tratar como admin do portal
    if (!userRole && PERFIS_INTERNOS.includes(qualifAtual ?? "")) {
      setRole("admin");
    }
    if (qualifAtual) setQualificacaoPerfil(qualifAtual);

    const extPortal =
      qualifAtual === "institution_external" || qualifAtual === "sindirepa_only";
    setPortalUsuarioEhExternoDaInstituicao(Boolean(extPortal));

    if (userId && paramId) {
      const { data: iuRow } = await supabase
        .from("institution_users")
        .select("cargo, unit_id")
        .eq("institution_id", paramId)
        .eq("user_id", userId)
        .maybeSingle();
      setMemberCargo(iuRow?.cargo?.trim() ? iuRow.cargo.trim() : null);
      const uid = iuRow?.unit_id;
      setMemberInstitutionUnitId(typeof uid === "string" && uid.trim() ? uid.trim() : null);
    } else {
      setMemberCargo(null);
      setMemberInstitutionUnitId(null);
    }
  }, [paramId, userId, navigate]);

  useEffect(() => {
    if (!paramId) return;
    let cancelado = false;
    setCarregando(true);
    setErro(false);

    void (async () => {
      await recarregarInstituicao();
      if (cancelado) return;
      setCarregando(false);
    })();

    return () => {
      cancelado = true;
    };
  }, [paramId, recarregarInstituicao]);

  useEffect(() => {
    if (!userId) return;
    supabase
      .rpc("instituicoes_do_usuario", {})
      .then(({ data }) => {
        const lista = Array.isArray(data) ? data : [];
        setTemMultiplasInstituicoes(lista.length > 1);
      })
      .catch(() => setTemMultiplasInstituicoes(false));
  }, [userId]);

  const handleSair = async () => {
    await supabase.auth.signOut();
    navigate("/acesso");
  };

  const voltarAoEscritorio = () => {
    if (paramId) navigate(`/dashboard/institutions/${paramId}`);
    else navigate("/dashboard/institutions");
  };

  const primaryColor = institution?.theme_colors?.primary ?? "#791614";
  const accentColor = institution?.theme_colors?.secondary ?? "#d7b369";
  const textColor = institution?.theme_colors?.text ?? "#f9f6f0";
  const temaPortalStyle = useMemo(
    () => ({
      ...estiloVariaveisTemaPortal(primaryColor, accentColor, textColor),
      background: "var(--pt-surface-gradient)",
    }),
    [primaryColor, accentColor, textColor],
  );

  if (carregando) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background:
            "radial-gradient(ellipse at top left, rgba(121,22,20,0.06) 0%, transparent 45%), radial-gradient(ellipse at bottom right, rgba(215,179,105,0.10) 0%, transparent 50%), linear-gradient(180deg, #fbf9f3 0%, #f5f1e6 100%)",
        }}
      >
        <div className="text-center">
          <div className="relative w-12 h-12 mx-auto mb-4">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                border: "2px solid rgba(121,22,20,0.10)",
              }}
            />
            <div
              className="absolute inset-0 rounded-full border-2 border-transparent animate-spin"
              style={{
                borderTopColor: "#791614",
                borderRightColor: "rgba(121,22,20,0.4)",
              }}
            />
          </div>
          <p
            className="font-accent uppercase mb-1"
            style={{
              fontSize: 10.5,
              letterSpacing: "0.22em",
              color: "#791614",
              opacity: 0.7,
              fontWeight: 600,
            }}
          >
            Ferrugini Advogados
          </p>
          <p
            className="font-body"
            style={{ fontSize: 14, color: "rgba(60,50,45,0.7)" }}
          >
            Carregando portal...
          </p>
        </div>
      </div>
    );
  }

  if (erro || !institution) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{
          background:
            "radial-gradient(ellipse at top left, rgba(121,22,20,0.06) 0%, transparent 45%), radial-gradient(ellipse at bottom right, rgba(215,179,105,0.10) 0%, transparent 50%), linear-gradient(180deg, #fbf9f3 0%, #f5f1e6 100%)",
        }}
      >
        <div
          className="text-center max-w-sm rounded-[16px] p-8"
          style={{
            background: "rgba(255,255,255,0.92)",
            border: "1px solid rgba(169,171,174,0.30)",
            boxShadow:
              "0 12px 36px -12px rgba(121,22,20,0.15), inset 0 1px 0 rgba(255,255,255,0.6)",
          }}
        >
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{
              background:
                "linear-gradient(135deg, rgba(121,22,20,0.12) 0%, rgba(121,22,20,0.04) 100%)",
              border: "1px solid rgba(121,22,20,0.20)",
            }}
          >
            <Building2 className="h-7 w-7 text-[#791614]" strokeWidth={1.4} />
          </div>
          <h2
            className="font-display text-[22px] font-semibold mb-2"
            style={{ color: "#791614" }}
          >
            Instituição não encontrada
          </h2>
          <p
            className="font-body mb-5 leading-relaxed"
            style={{ fontSize: 14, color: "rgba(60,50,45,0.7)" }}
          >
            Não foi possível carregar os dados da instituição. Verifique sua conexão ou entre em contato com o escritório.
          </p>
          <Button variant="outline" onClick={handleSair} className="gap-2">
            <LogOut className="h-3.5 w-3.5" /> Sair
          </Button>
        </div>
      </div>
    );
  }

  const nomeExibicao = institution.nome_fantasia?.trim() || institution.name;
  const iniciais = nomeExibicao.trim().slice(0, 2).toUpperCase() || "—";

  return (
    <div className="min-h-screen flex flex-col" style={temaPortalStyle}>
      {/* ── Topbar ── */}
      <header
        className="shrink-0 flex items-center justify-between px-6 md:px-7 relative"
        style={{
          height: 64,
          background:
            "linear-gradient(135deg, var(--pt-primary) 0%, var(--pt-primary) 55%, color-mix(in srgb, var(--pt-primary) 80%, var(--pt-accent) 20%) 100%)",
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.06) inset, 0 6px 18px -8px rgba(0,0,0,0.35)",
        }}
      >
        {/* Filete dourado inferior — assinatura visual */}
        <span
          aria-hidden
          className="absolute left-0 right-0 bottom-0 h-[1.5px] pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, transparent 0%, var(--pt-accent-50) 25%, var(--pt-accent) 50%, var(--pt-accent-50) 75%, transparent 100%)",
            opacity: 0.85,
          }}
        />

        {/* Esquerda: logo + nome */}
        <div className="flex items-center gap-3 min-w-0">
          {institution.logo_url ? (
            <img
              src={institution.logo_url}
              alt={nomeExibicao}
              className="h-[42px] w-[42px] rounded-[10px] object-cover shrink-0"
              style={{
                border: "1px solid var(--pt-accent-40)",
                background: "var(--pt-accent-08)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              }}
            />
          ) : (
            <div
              className="h-[42px] w-[42px] rounded-[10px] flex items-center justify-center shrink-0"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.05) 100%)",
                border: "1px solid var(--pt-accent-40)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.08), 0 2px 6px rgba(0,0,0,0.2)",
              }}
            >
              <span
                className="font-display leading-none font-semibold"
                style={{ fontSize: 16, color: "var(--pt-accent)" }}
              >
                {iniciais}
              </span>
            </div>
          )}
          <div className="min-w-0">
            <h1
              className="font-display leading-tight font-semibold truncate"
              style={{ fontSize: 18, color: "var(--pt-text)", letterSpacing: "0.005em" }}
            >
              {nomeExibicao}
            </h1>
            {institution.type && (
              <p
                className="font-accent uppercase leading-none mt-[3px] flex items-center gap-2"
                style={{ fontSize: 10, letterSpacing: "0.18em", color: "var(--pt-accent)" }}
              >
                <span style={{ opacity: 0.95 }}>
                  {institution.type.replace(/_/g, " ")}
                </span>
                <span
                  aria-hidden
                  className="inline-block w-[3px] h-[3px] rotate-45"
                  style={{ background: "var(--pt-accent-60)" }}
                />
                <span style={{ color: "var(--pt-text-55)" }}>Portal</span>
              </p>
            )}
          </div>
        </div>

        {/* Direita: e-mail + trocar + separador + botão */}
        <div className="flex items-center gap-3">
          {user?.email && (
            <span
              className="font-body hidden md:block px-3 py-1 rounded-full"
              style={{
                fontSize: 12.5,
                color: "var(--pt-text-70)",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              {user.email}
            </span>
          )}
          {temMultiplasInstituicoes && (
            <>
              <button
                type="button"
                className="hidden sm:flex items-center gap-1.5 font-body transition-all"
                style={{
                  fontSize: 12.5,
                  color: "var(--pt-text-70)",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  padding: "6px 12px",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.10)";
                  e.currentTarget.style.color = "var(--pt-text)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                  e.currentTarget.style.color = "var(--pt-text-70)";
                }}
                onClick={() => navigate("/acesso-externo/portal")}
                title="Trocar de portal"
              >
                <ArrowLeftRight className="h-[14px] w-[14px]" />
                Trocar
              </button>
              <div
                className="hidden sm:block"
                style={{ width: 1, height: 22, background: "rgba(255,255,255,0.14)" }}
              />
            </>
          )}
          {exibirVoltarEscritorio ? (
            <button
              type="button"
              className="flex items-center gap-1.5 font-body transition-all"
              style={{
                fontSize: 12.5,
                color: "var(--pt-text)",
                background:
                  "linear-gradient(135deg, var(--pt-accent-22) 0%, var(--pt-accent-12) 100%)",
                border: "1px solid var(--pt-accent-40)",
                padding: "7px 14px",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 500,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background =
                  "linear-gradient(135deg, var(--pt-accent-30) 0%, var(--pt-accent-18) 100%)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background =
                  "linear-gradient(135deg, var(--pt-accent-22) 0%, var(--pt-accent-12) 100%)";
              }}
              onClick={voltarAoEscritorio}
            >
              <ArrowLeft className="h-[14px] w-[14px]" />
              Voltar ao escritório
            </button>
          ) : (
            <button
              type="button"
              className="flex items-center gap-1.5 font-body transition-all"
              style={{
                fontSize: 12.5,
                color: "var(--pt-text)",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.16)",
                padding: "7px 14px",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 500,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.12)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.24)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.16)";
              }}
              onClick={() => void handleSair()}
            >
              <LogOut className="h-[14px] w-[14px]" />
              Sair
            </button>
          )}
        </div>
      </header>

      {/* ── Conteúdo ── */}
      <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <Outlet
          context={{
            institution,
            role,
            memberCargo,
            memberInstitutionUnitId,
            portalUsuarioEhExternoDaInstituicao,
            recarregarInstituicao,
          } satisfies ExternalLayoutContext}
        />
      </main>

      {/* ── Footer ── */}
      <footer
        className="shrink-0 flex items-center justify-center relative"
        style={{
          height: 36,
          background:
            "linear-gradient(180deg, color-mix(in srgb, var(--pt-primary) 96%, black 4%) 0%, var(--pt-primary) 100%)",
        }}
      >
        {/* Filete dourado superior */}
        <span
          aria-hidden
          className="absolute left-0 right-0 top-0 h-px pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, transparent, var(--pt-accent-50) 50%, transparent)",
            opacity: 0.6,
          }}
        />
        <div className="flex items-center gap-0">
          <span
            className="font-accent uppercase"
            style={{ fontSize: 10, letterSpacing: "0.18em", color: "var(--pt-text-55)" }}
          >
            Área restrita
          </span>
          <div
            style={{
              width: 4,
              height: 4,
              background: "var(--pt-accent)",
              transform: "rotate(45deg)",
              margin: "0 12px",
              opacity: 0.55,
            }}
          />
          <span
            className="font-display"
            style={{
              fontSize: 12,
              letterSpacing: "0.04em",
              color: "var(--pt-text-70)",
              fontStyle: "italic",
            }}
          >
            Ferrugini Advogados
          </span>
          <div
            style={{
              width: 4,
              height: 4,
              background: "var(--pt-accent)",
              transform: "rotate(45deg)",
              margin: "0 12px",
              opacity: 0.55,
            }}
          />
          <span
            className="font-accent uppercase"
            style={{ fontSize: 10, letterSpacing: "0.18em", color: "var(--pt-text-55)" }}
          >
            Acessos registrados
          </span>
        </div>
      </footer>
    </div>
  );
}
